import { create } from 'zustand'
import type { Chat, Message } from '../types'

function sortChats(chats: Chat[]): Chat[] {
  return [...chats].sort((a, b) => {
    if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1
    const aTime = a.last_message?.created_at ?? a.created_at ?? ''
    const bTime = b.last_message?.created_at ?? b.created_at ?? ''
    return bTime < aTime ? -1 : bTime > aTime ? 1 : 0
  })
}

interface ChatState {
  chats: Chat[]
  activeChatId: string | null
  messages: Record<string, Message[]>      // chatId → messages (sorted asc)
  nextCursor: Record<string, string | null> // chatId → cursor для загрузки старых
  typingUsers: Record<string, Set<string>> // chatId → userIds typing
  unread: Record<string, number>           // chatId → count

  setChats: (chats: Chat[]) => void
  upsertChat: (chat: Chat) => void
  removeChat: (id: string) => void
  setActiveChat: (id: string | null) => void

  setMessages: (chatId: string, messages: Message[]) => void
  prependMessages: (chatId: string, messages: Message[]) => void // пагинация вверх
  appendMessage: (msg: Message) => void
  updateMessage: (chatId: string, messageId: string, patch: Partial<Message>) => void
  removeMessage: (chatId: string, messageId: string) => void

  setNextCursor: (chatId: string, cursor: string | null) => void

  setTyping: (chatId: string, userId: string) => void
  clearTyping: (chatId: string, userId: string) => void

  incrementUnread: (chatId: string) => void
  resetUnread: (chatId: string) => void
  setUnread: (chatId: string, count: number) => void

  updateLastMessage: (chatId: string, msg: Message) => void
}

export const useChatStore = create<ChatState>()((set) => ({
  chats: [],
  activeChatId: null,
  messages: {},
  nextCursor: {},
  typingUsers: {},
  unread: {},

  setChats: (chats) =>
    set((s) => {
      // Sync unread from server: take max(server, local) so WS increments aren't lost
      const unread = { ...s.unread }
      for (const chat of chats) {
        const server = chat.unread_count ?? 0
        const local = unread[chat.id] ?? 0
        unread[chat.id] = Math.max(server, local)
      }
      return { chats: sortChats(chats), unread }
    }),

  upsertChat: (chat) =>
    set((s) => {
      const idx = s.chats.findIndex((c) => c.id === chat.id)
      const chats = [...s.chats]
      if (idx >= 0) chats[idx] = { ...chats[idx], ...chat }
      else chats.unshift(chat)
      const unread = { ...s.unread }
      const server = chat.unread_count ?? 0
      unread[chat.id] = Math.max(server, unread[chat.id] ?? 0)
      return { chats: sortChats(chats), unread }
    }),

  removeChat: (id) =>
    set((s) => {
      const unread = { ...s.unread }
      delete unread[id]
      return {
        chats: s.chats.filter((c) => c.id !== id),
        activeChatId: s.activeChatId === id ? null : s.activeChatId,
        unread,
      }
    }),

  // Don't reset unread here — MessageList calls resetUnread when messages are
  // actually visible. Resetting on navigation would clear badges before reading.
  setActiveChat: (id) => set({ activeChatId: id }),

  setMessages: (chatId, messages) =>
    set((s) => ({ messages: { ...s.messages, [chatId]: messages } })),

  prependMessages: (chatId, older) =>
    set((s) => ({
      messages: { ...s.messages, [chatId]: [...older, ...(s.messages[chatId] ?? [])] },
    })),

  appendMessage: (msg) =>
    set((s) => {
      const existing = s.messages[msg.chat_id] ?? []
      // дедупликация
      if (existing.some((m) => m.id === msg.id)) return s
      return { messages: { ...s.messages, [msg.chat_id]: [...existing, msg] } }
    }),

  updateMessage: (chatId, messageId, patch) =>
    set((s) => ({
      messages: {
        ...s.messages,
        [chatId]: (s.messages[chatId] ?? []).map((m) =>
          m.id === messageId ? { ...m, ...patch } : m,
        ),
      },
    })),

  removeMessage: (chatId, messageId) =>
    set((s) => ({
      messages: {
        ...s.messages,
        [chatId]: (s.messages[chatId] ?? []).filter((m) => m.id !== messageId),
      },
    })),

  setNextCursor: (chatId, cursor) =>
    set((s) => ({ nextCursor: { ...s.nextCursor, [chatId]: cursor } })),

  setTyping: (chatId, userId) =>
    set((s) => {
      const set_ = new Set(s.typingUsers[chatId])
      set_.add(userId)
      return { typingUsers: { ...s.typingUsers, [chatId]: set_ } }
    }),

  clearTyping: (chatId, userId) =>
    set((s) => {
      const set_ = new Set(s.typingUsers[chatId])
      set_.delete(userId)
      return { typingUsers: { ...s.typingUsers, [chatId]: set_ } }
    }),

  incrementUnread: (chatId) =>
    set((s) => {
      const newCount = (s.unread[chatId] ?? 0) + 1
      return {
        unread: { ...s.unread, [chatId]: newCount },
        chats: s.chats.map((c) => (c.id === chatId ? { ...c, unread_count: newCount } : c)),
      }
    }),

  resetUnread: (chatId) =>
    set((s) => ({
      unread: { ...s.unread, [chatId]: 0 },
      chats: s.chats.map((c) => (c.id === chatId ? { ...c, unread_count: 0 } : c)),
    })),

  setUnread: (chatId, count) =>
    set((s) => ({
      unread: { ...s.unread, [chatId]: count },
      chats: s.chats.map((c) => (c.id === chatId ? { ...c, unread_count: count } : c)),
    })),

  updateLastMessage: (chatId, msg) =>
    set((s) => ({
      chats: sortChats(
        s.chats.map((c) => (c.id === chatId ? { ...c, last_message: msg } : c)),
      ),
    })),
}))
